import { type MutableRefObject, useCallback } from "react";
import { useHistory } from "react-router-dom";

import { APP_V2_APP_EVENTS, logError, logEvent } from "../../../lib/analytics";
import { showErrorToast } from "../../../lib/Notifications";
import { completeMagicLinkSignIn } from "../../cognito/cognitoAuth";
import { initCognitoSession } from "../../cognito/initCognitoSession";
import { isSafeRedirectUrl } from "../../cognito/isSafeRedirectUrl";
import { clearMagicLinkEmail } from "../../cognito/magicLinkEmailStore";
import { mapCognitoError } from "../../cognito/mapCognitoError";
import {
  classifyCognitoError,
  IDENTITY_PROVIDER,
  isNetworkFailure,
  LOGIN_OUTCOME,
  LOGIN_REASON,
  setLoginIdentityProvider,
} from "../../loginRum";
import { type AuthState, type EmailSource } from "../types";
import { MAGIC_LINK_REDIRECT_IN_PROGRESS_KEY } from "./useResolveInitialAuthState";

const DEFAULT_REDIRECT_PATH = "/";

interface UseMagicLinkRedeemArguments {
  payload: string | null;
  searchParams: URLSearchParams;
  hasAttemptedAuth: MutableRefObject<boolean>;
  setAuthState: (state: AuthState) => void;
  setError: (error: string | undefined) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Redux dispatch
  dispatch: any;
}

/**
 * Encapsulates the magic-link redemption flow — Cognito sign-in, Redux
 * session hydration, and final navigation. Returns a stable callback
 * AuthRedirect wires into its button handlers.
 *
 * Extracted from AuthRedirect.tsx to keep that file under the max-lines
 * and max-dependencies limits.
 */
export function useMagicLinkRedeem({
  payload,
  searchParams,
  hasAttemptedAuth,
  setAuthState,
  setError,
  dispatch,
}: UseMagicLinkRedeemArguments): (emailToUse: string, source: EmailSource) => Promise<void> {
  const history = useHistory();

  return useCallback(
    async (emailToUse: string, source: EmailSource) => {
      if (hasAttemptedAuth.current) {
        return;
      }
      hasAttemptedAuth.current = true;

      setAuthState("signing-in");
      setLoginIdentityProvider(IDENTITY_PROVIDER.COGNITO_MAGIC_LINK);

      try {
        const requestedUrl = searchParams.get("requestedUrl");
        logEvent(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_STARTED);

        if (!payload) {
          logError(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_FAILURE, {
            error: new Error("Missing payload in magic link"),
            metadata: {
              errorCode: "auth/invalid-link",
              errorMessage: "Missing payload",
              outcome: LOGIN_OUTCOME.EXPECTED_ERROR,
              reason: "auth/invalid-link",
            },
          });
          setError("This login link is invalid or incomplete. Please request a new login link.");
          setAuthState("error");
          return;
        }

        logEvent(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_EMAIL_RESOLVED, { source });

        await completeMagicLinkSignIn({ payload, email: emailToUse });
        logEvent(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_COGNITO_SUCCESS);

        setAuthState("redirecting");
        // Update the longstanding "remember last user" key for pre-fill on
        // future visits.
        localStorage.setItem("emailForSignIn", emailToUse);

        const target =
          requestedUrl && isSafeRedirectUrl(requestedUrl) ? requestedUrl : DEFAULT_REDIRECT_PATH;

        // Flag the impending redirect BEFORE dispatching the session thunk —
        // the dispatch flips `isAuthorized` mid-await, causing the parent
        // route tree to remount AuthRedirect. The fresh instance reads this
        // flag and stays in the loading state instead of rendering the
        // "already authenticated" consent screen.
        sessionStorage.setItem(MAGIC_LINK_REDIRECT_IN_PROGRESS_KEY, "1");

        // Hydrate the Redux session before navigating so PrivateRoute sees a
        // valid session immediately, avoiding a flash of the login page.
        await dispatch(initCognitoSession());

        logEvent(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_SUCCESS, {
          outcome: LOGIN_OUTCOME.SUCCESS,
        });
        history.replace(target);
        sessionStorage.removeItem(MAGIC_LINK_REDIRECT_IN_PROGRESS_KEY);
        clearMagicLinkEmail();
      } catch (authError: unknown) {
        hasAttemptedAuth.current = false;
        sessionStorage.removeItem(MAGIC_LINK_REDIRECT_IN_PROGRESS_KEY);
        const mappedError = mapCognitoError(authError);
        const { outcome, reason } =
          mappedError.code === "auth/unknown" && isNetworkFailure(authError)
            ? { outcome: LOGIN_OUTCOME.EXPECTED_ERROR, reason: LOGIN_REASON.NETWORK_FAILURE }
            : classifyCognitoError(mappedError.code);

        logError(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_FAILURE, {
          error: authError,
          metadata: {
            errorCode: mappedError.code,
            errorMessage: mappedError.message,
            email: emailToUse,
            outcome,
            reason,
          },
        });
        if (mappedError.code === "auth/user-type-not-allowed") {
          logEvent(APP_V2_APP_EVENTS.COGNITO_AUTH_USER_TYPE_NOT_ALLOWED, { email: emailToUse });
        }

        // Show a link-expired message for generic/unknown errors. Surface
        // the mapped message otherwise (e.g. user-not-found,
        // password-reset-required). We deliberately don't reveal whether a
        // typed email matched or not — that would let an attacker
        // brute-force the link's target by trying emails until they stop
        // getting an error.
        const errorMessage =
          mappedError.code === "auth/unknown"
            ? "Link verification failed. The link may have expired. Please request a new login link."
            : mappedError.message;

        setError(errorMessage);
        setAuthState("error");
        showErrorToast(errorMessage);
      }
    },
    [payload, searchParams, history, dispatch, hasAttemptedAuth, setAuthState, setError]
  );
}
